import { useEffect, useState } from "react";
import { Box, TextField, Typography, useTheme, useMediaQuery } from "@mui/material";
import Dialog from "../../Common/Dailog/Dailog";
import FormInput from "../../Common/FormInputs/MuiTextField";
import { useDesignTokens } from "../../../theme/useDesignTokens";
import {
  blockInvalidNumberKeys,
  getPositiveIntegerSlotProps,
} from "../../../constants/loyaltyConstant";
import { MAX_POSITIVE_INT_LENGTH } from "../../../utils/general";
import {
  addLoyaltySpecData,
  updateLoyaltySpecData,
} from "../../../services/loyaltySpecService";
import customConfig from "../../../config";
import FullScreenLoader from "../../Common/ScreenLoader/FullScreenLoader";

const COMMON_TEXT = {
  positiveHelp: "Positive numbers only (1 and above)",
  positiveError: "Must be a valid number and greater than 0",
};

const FORM_CONFIG = {
  action: {
    label: "Action Id",
    type: "text",
    placeholder: "e.g., app.user.register",
    helpText: "Letters and dots only (3-40 characters)",
  },
  reward: {
    label: "Reward",
    type: "text",
    placeholder: "e.g., 50",
    helpText: COMMON_TEXT.positiveHelp,
  },
  expiry: {
    label: "Expiry (In Days)",
    type: "text",
    placeholder: "e.g., 30",
    helpText: COMMON_TEXT.positiveHelp,
  },
  config: {
    label: "Reward Config (JSON)",
    placeholder: "e.g., { 'maxPerDay': 1 }",
    rows: 6,
    helpText: "Valid JSON format is required",
  },
};

const VALIDATION = {
  action: {
    min: 3,
    max: 40,
    regex: /^(?=.*[a-zA-Z])(?=.*\.)[a-zA-Z.]+$/,
    messages: {
      required: "Action Id is required",
      regex: "Only contain letters and at least one dot (.)",
      min: "Minimum length is 3 characters",
      max: "Maximum length is 40 characters",
    },
  },
  number: {
    min: 1,
    error: COMMON_TEXT.positiveError,
  },
  json: {
    error: "Invalid JSON format",
  },
};

const INITIAL_FORM_STATE = {
  action: "",
  reward: {
    fixed: "",
    expiryInDays: "",
  },
  config: "{}",
};

const AddAndUpdateSpec = ({ open, onClose, initialData, feedbackHandler }) => {
  const { colors, border } = useDesignTokens();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [msgToDisplay, setMsgToDisplay] = useState(null);

  useEffect(() => {
    if (initialData) {
      setFormData({
        action: initialData?.action || "",
        reward: {
          fixed:
            initialData?.reward?.fixed != null // != null checks both null and undefined so it is equivalent to value !== null && value !== undefined
              ? String(initialData?.reward?.fixed)
              : "",
          expiryInDays:
            initialData?.reward?.expiryInDays != null // != null checks both null and undefined so it is equivalent to value !== null && value !== undefined
              ? String(initialData?.reward?.expiryInDays)
              : "",
        },
        config:
          initialData?.config != null &&
          typeof initialData?.config === "object" &&
          !Array.isArray(initialData?.config)
            ? JSON.stringify(initialData?.config)
            : "{}",
      });
    } else {
      setFormData(INITIAL_FORM_STATE);
    }

    setErrors({});
    setTouched({});
  }, [initialData, open]);

  const validateAction = (value) => {
    if (!value.trim()) return VALIDATION.action.messages.required;
    if (!VALIDATION.action.regex.test(value))
      return VALIDATION.action.messages.regex;
    if (value.length < VALIDATION.action.min)
      return VALIDATION.action.messages.min;
    if (value.length > VALIDATION.action.max)
      return VALIDATION.action.messages.max;
    return "";
  };

  const validatePositiveInteger = (value) => {
    if (value === "" || value === null || value === undefined || !value) {
      return VALIDATION.number.error;
    }
    // Disallow decimals, negatives, zero, non-numbers
    if (!/^(0*[1-9]\d*)$/.test(String(value))) {
      return VALIDATION.number.error;
    }
    if (String(value).length > MAX_POSITIVE_INT_LENGTH) {
      return VALIDATION.number.error;
    }
    return "";
  };

  const hasOnlyStringKeys = (obj) => {
    if (Array.isArray(obj)) return true;
    if (typeof obj === "object" && obj !== null) {
      return Object.keys(obj).every(
        (key) => typeof key === "string" && hasOnlyStringKeys(obj[key]),
      );
    }
    return true;
  };

  const validateConfig = (value) => {
    if (!value || !value.trim()) {
      return VALIDATION.json.error;
    }
    try {
      const parsed = JSON.parse(value);
      if (!hasOnlyStringKeys(parsed)) {
        return VALIDATION.json.error;
      }
      // Root must be a plain object (not array, not null)
      if (
        typeof parsed !== "object" ||
        parsed === null ||
        Array.isArray(parsed)
      ) {
        return VALIDATION.json.error;
      }
      return "";
    } catch {
      return VALIDATION.json.error;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("reward.")) {
      const field = name.replace("reward.", "");
      setFormData((prev) => ({
        ...prev,
        reward: { ...prev.reward, [field]: value },
      }));

      const error = validatePositiveInteger(value);
      setErrors((prev) => ({ ...prev, [field]: error }));
      if (error && error !== "") {
        setTouched((prev) => ({ ...prev, [field]: "true" }));
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "action") {
      const error = validateAction(value);
      setErrors((prev) => ({ ...prev, action: error }));
      if (error && error !== "") {
        setTouched((prev) => ({ ...prev, action: "true" }));
      }
    }

    if (name === "config") {
      const error = validateConfig(value);
      setErrors((prev) => ({ ...prev, config: error }));
      if (error && error !== "") {
        setTouched((prev) => ({ ...prev, config: "true" }));
      }
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: "true" }));
  };

  const handleSave = async () => {
    const actionError = validateAction(formData.action);
    const rewardError = validatePositiveInteger(formData.reward.fixed);
    const expiryError = validatePositiveInteger(formData.reward.expiryInDays);
    const jsonError = validateConfig(formData.config);

    const nextErrors = {
      action: actionError,
      fixed: rewardError,
      expiryInDays: expiryError,
      config: jsonError,
    };

    setErrors(nextErrors);

    if (actionError || rewardError || expiryError || jsonError) return;

    let body = {
      action: formData.action?.trim()?.toLocaleLowerCase(),
      config: JSON.parse(formData.config?.trim()),
      reward: {
        fixed: Number(formData.reward.fixed),
        expiryInDays: Number(formData.reward.expiryInDays),
      },
    };

    setLoading(true);
    try {
      let response = null;
      if (!initialData) {
        setMsgToDisplay(customConfig.ADDING_DATA);
        response = await addLoyaltySpecData({ body });
      } else {
        setMsgToDisplay(customConfig.UPDATING_DATA);
        response = await updateLoyaltySpecData({ body });
      }

      setLoading(false);
      if (response) {
        if (response?.success) {
          feedbackHandler({
            open: true,
            type: "success",
            msg: response?.message,
          });
          onClose(response);
        } else {
          feedbackHandler({
            open: true,
            type: "error",
            msg: response?.message,
          });
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    !validateAction(formData.action) &&
    !validatePositiveInteger(formData.reward.fixed) &&
    !validatePositiveInteger(formData.reward.expiryInDays) &&
    !validateConfig(formData.config);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={initialData ? "Edit Loyalty Spec" : "Add New Loyalty Spec"}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          [theme.breakpoints.down("md")]: {
            maxWidth: "calc(100% - 16px)",
            margin: theme.spacing(1),
            marginTop: "72px",
            maxHeight: "calc(100vh - 88px)",
          },
        },
      }}
      sx={{
        zIndex: 10001,
        "& .MuiDialogContent-root": {
          [theme.breakpoints.down("md")]: {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
            overflowY: "auto",
          },
        },
        "& .MuiDialogActions-root": {
          [theme.breakpoints.down("md")]: {
            flexWrap: "wrap",
            padding: theme.spacing(2),
          },
        },
      }}
      actions={[
        { label: "Cancel", variant: "ghost", onClick: onClose },
        {
          label: initialData ? "Update Spec" : "Add Spec",
          tooltip: !isFormValid
            ? "Please complete all required fields and resolve any validation errors to proceed."
            : initialData
              ? "Click to update the spec."
              : "Click to add a new spec.",
          variant: "primary",
          onClick: handleSave,
          disabled: !isFormValid,
          slotProps: {
            popper: {
              sx: { zIndex: 10002 },
            },
          },
        },
      ]}
    >
      {loading && <FullScreenLoader message={msgToDisplay} />}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 2 }}>
        <FormInput
          id="action"
          type={FORM_CONFIG.action.type}
          label={FORM_CONFIG.action.label}
          name="action"
          value={formData.action}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={FORM_CONFIG.action.placeholder}
          error={!!errors.action}
          helperText={errors.action || FORM_CONFIG.action.helpText}
          disabled={!!initialData}
          touched={touched.action}
          fullWidth
          slotProps={{
            htmlInput: {
              maxLength: 40,
            },
          }}
        />

        <FormInput
          id="reward.fixed"
          type={FORM_CONFIG.reward.type}
          label={FORM_CONFIG.reward.label}
          name="reward.fixed"
          value={formData.reward.fixed}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={blockInvalidNumberKeys}
          placeholder={FORM_CONFIG.reward.placeholder}
          error={!!errors.fixed}
          helperText={errors.fixed || FORM_CONFIG.reward.helpText}
          touched={touched["reward.fixed"]}
          fullWidth
          slotProps={getPositiveIntegerSlotProps()}
        />

        <FormInput
          id="reward.expiryInDays"
          type={FORM_CONFIG.expiry.type}
          label={FORM_CONFIG.expiry.label}
          name="reward.expiryInDays"
          value={formData.reward.expiryInDays}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={blockInvalidNumberKeys}
          placeholder={FORM_CONFIG.expiry.placeholder}
          error={!!errors.expiryInDays}
          helperText={errors.expiryInDays || FORM_CONFIG.expiry.helpText}
          touched={touched["reward.expiryInDays"]}
          fullWidth
          slotProps={getPositiveIntegerSlotProps()}
          // endAdornment={"Days"}
        />

        <Box>
          <Typography variant="subtitle2">
            {FORM_CONFIG.config.label}
          </Typography>
          <TextField
            id="config"
            name="config"
            type="textarea"
            multiline
            rows={isMobile ? 4 : FORM_CONFIG.config.rows}
            fullWidth
            value={formData.config}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={FORM_CONFIG.config.placeholder}
            error={!!errors.config}
            helperText={errors.config || FORM_CONFIG.config.helpText}
            touched={touched.config}
            sx={{
              "& .MuiOutlinedInput-input": {
                padding: "0px !important",
                height: isMobile ? "80px !important" : "130px !important",
              },
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: `${colors.primary.main} !important`,
                borderWidth: `${border.twoPx}!important`,
              },
            }}
            slotProps={{
              htmlInput: {
                maxLength: 10000,
              },
            }}
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default AddAndUpdateSpec;
